import { compareCategoryNamesForDisplay } from '../../../../../services/categories/category-order.util';
import type { ProductView } from '../../../../../services/products/products.service';

export type ProductSortKey = 'name' | 'price' | 'category' | 'status' | 'printers' | 'kds';
export type ProductSortDir = 'asc' | 'desc';
export type KdsFilter = 'all' | 'on' | 'off';
export type StatusFilter = 'all' | 'active' | 'inactive';

export type ProductFilters = {
  categoryIds: number[];
  printerIds: number[];
  kds: KdsFilter;
  status: StatusFilter;
};

export const EMPTY_PRODUCT_FILTERS: ProductFilters = { categoryIds: [], printerIds: [], kds: 'all', status: 'all' };

// Architectural summary: true when any filter or search narrows the list (enables clear-filters).
export function hasActiveFilters(filters: ProductFilters, normalizedQuery: string): boolean {
  return (
    normalizedQuery.length > 0 ||
    filters.categoryIds.length > 0 ||
    filters.printerIds.length > 0 ||
    filters.kds !== 'all' ||
    filters.status !== 'all'
  );
}

// Architectural summary: search haystack = name + price + category name, lowercased (M11).
export function matchesSearch(
  product: ProductView,
  normalizedQuery: string,
  categoryNameById: Map<number, string>,
): boolean {
  if (!normalizedQuery) return true;
  const categoryName = product.categoryId !== null ? categoryNameById.get(product.categoryId) ?? '' : '';
  return `${product.name} ${product.price} ${categoryName}`.toLowerCase().includes(normalizedQuery);
}

// Architectural summary: applies search + filters in one pass; empty dimensions match all.
export function filterProducts(
  products: ProductView[],
  filters: ProductFilters,
  normalizedQuery: string,
  categoryNameById: Map<number, string>,
): ProductView[] {
  return products.filter((product) => {
    if (!matchesSearch(product, normalizedQuery, categoryNameById)) return false;
    if (filters.status === 'active' && !product.isActive) return false;
    if (filters.status === 'inactive' && product.isActive) return false;
    if (filters.kds === 'on' && !product.kds) return false;
    if (filters.kds === 'off' && product.kds) return false;
    if (
      filters.categoryIds.length > 0 &&
      (product.categoryId === null || !filters.categoryIds.includes(product.categoryId))
    ) {
      return false;
    }
    if (filters.printerIds.length > 0 && !filters.printerIds.some((id) => product.printerIds.includes(id))) {
      return false;
    }
    return true;
  });
}

function compareByKey(
  a: ProductView,
  b: ProductView,
  sortKey: ProductSortKey,
  categoryNameById: Map<number, string>,
  printerNameById: Map<number, string>,
): number {
  const printerLabel = (product: ProductView): string => {
    if (product.printerIds.length === 0) {
      return '';
    }
    return product.printerIds
      .map((id) => printerNameById.get(id) ?? `#${id}`)
      .sort((left, right) => left.localeCompare(right, 'he'))
      .join(', ');
  };

  switch (sortKey) {
    case 'name':
      return a.name.localeCompare(b.name, 'he');
    case 'price':
      return a.price - b.price;
    case 'category': {
      const nameA = a.categoryId !== null ? categoryNameById.get(a.categoryId) ?? null : null;
      const nameB = b.categoryId !== null ? categoryNameById.get(b.categoryId) ?? null : null;
      return compareCategoryNamesForDisplay(nameA, nameB);
    }
    case 'status':
      return a.isActive === b.isActive ? 0 : a.isActive ? -1 : 1;
    case 'printers':
      return printerLabel(a).localeCompare(printerLabel(b), 'he');
    case 'kds':
      return a.kds === b.kds ? 0 : a.kds ? -1 : 1;
    default:
      return 0;
  }
}

// Architectural summary: copy-sort by persisted display order (drag/clean view), id tiebreak.
export function sortBySortOrder(products: ProductView[]): ProductView[] {
  return [...products].sort((a, b) => a.sortOrder - b.sortOrder || a.id - b.id);
}

// Architectural summary: pure copy-sort by column; null key keeps input order, id breaks ties.
export function sortProducts(
  products: ProductView[],
  sortKey: ProductSortKey | null,
  sortDir: ProductSortDir,
  categoryNameById: Map<number, string>,
  printerNameById: Map<number, string>,
): ProductView[] {
  if (sortKey === null) return products;
  const factor = sortDir === 'asc' ? 1 : -1;
  return [...products].sort((a, b) => {
    const primary = compareByKey(a, b, sortKey, categoryNameById, printerNameById);
    if (primary !== 0) return factor * primary;
    return a.id - b.id;
  });
}
